import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackToTaxFormsBanner from '../components/BackToTaxFormsBanner';
import { getTaxYearOptions } from '../utils/taxYears';
import { DollarSign, Edit, Trash2, Building, TrendingUp, FileText } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import axios from 'axios';
import toast from 'react-hot-toast';

const IncomeTracker = () => {
  const [incomeTransactions, setIncomeTransactions] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({}); // Store all edits before saving
  const [duplicateModal, setDuplicateModal] = useState(null); // Store duplicates found
  const [suppressDuplicatesNextFetch, setSuppressDuplicatesNextFetch] = useState(false);
  const [incomeRules, setIncomeRules] = useState({}); // description -> { transaction_type, property_id }
  const [filters, setFilters] = useState({
    transaction_type: '',
    property_id: '',
    start_date: '',
    end_date: '',
    tax_year: '' // '' = All Years; otherwise filters by that year
  });

  useEffect(() => {
    fetchIncomeTransactions();
    fetchProperties();
    loadIncomeRules();
  }, [filters]);

  const normalizeDescription = (text) => {
    if (!text) return '';
    let s = String(text).toLowerCase();
    // Remove common date patterns
    s = s.replace(/\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/g, ''); // MM/DD/YYYY, M/D/YY, MM-DD-YYYY
    s = s.replace(/\b\d{4}-\d{2}-\d{2}\b/g, ''); // YYYY-MM-DD
    s = s.replace(/\b\d{1,2}[\/\-]\d{1,2}\b/g, ''); // MM/DD, M-D
    // Remove long numbers and masked sequences
    s = s.replace(/\d{10,}/g, ''); // long numeric tokens
    s = s.replace(/\*\d+/g, ''); // masked numbers like *1234
    // Normalize punctuation and separators
    s = s.replace(/[,:;|\-]+/g, ' '); // replace runs of separators with space
    // Collapse whitespace and trim
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  };

  // For duplicate detection: preserve transaction IDs (9–10 digit numbers like Zelle ref IDs)
  // so two payments from same person, same amount, same day are not falsely flagged
  const normalizeDescriptionForDuplicates = (text) => {
    if (!text) return '';
    let s = String(text).toLowerCase();
    s = s.replace(/\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/g, '');
    s = s.replace(/\b\d{4}-\d{2}-\d{2}\b/g, '');
    s = s.replace(/\b\d{1,2}[\/\-]\d{1,2}\b/g, '');
    s = s.replace(/\*\d+/g, ''); // masked numbers like *1234
    s = s.replace(/[,:;|\-]+/g, ' ');
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  };

  const loadIncomeRules = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
      const res = await axios.get('/api/learning-rules', { headers });
      if (res.data?.success) {
        const rules = res.data.data || res.data.rules || [];
        const map = {};
        rules.forEach((rule) => {
          const key = normalizeDescription(rule.pattern || rule.displayName || '');
          // Only consider rules that have income-related metadata
          const addl = rule.additionalData || {};
          const txType = addl.transaction_type || addl.incomeType || addl.txType;
          const propertyId = addl.property_id || addl.propertyId;
          if (key && txType) {
            map[key] = { transaction_type: txType, property_id: propertyId || null };
          }
        });
        setIncomeRules(map);
      }
    } catch (e) {
      // Silent fail; rules optional
    }
  };

  const detectDuplicates = (transactions) => {
    const duplicates = [];
    const seen = new Map(); // Map of "date|description|amount" -> transaction
    
    transactions.forEach((tx, index) => {
      // Normalize date (remove time if present)
      const dateKey = tx.transaction_date ? tx.transaction_date.split('T')[0] : '';
      // Use description that preserves transaction IDs (Zelle ref, etc.) so distinct payments aren't flagged
      const descKey = normalizeDescriptionForDuplicates(tx.description);
      // Include amount so we only flag true duplicates (same transaction imported twice)
      const amountKey = (parseFloat(tx.amount) || 0).toFixed(2);
      
      const key = `${dateKey}|${descKey}|${amountKey}`;
      
      if (seen.has(key)) {
        // Found a duplicate
        const existingTx = seen.get(key);
        if (!existingTx._duplicateMarked) {
          // First occurrence of this duplicate set
          duplicates.push({
            transaction: existingTx,
            index: existingTx._originalIndex,
            duplicateOf: null
          });
          existingTx._duplicateMarked = true;
        }
        duplicates.push({
          transaction: tx,
          index: index,
          duplicateOf: existingTx
        });
      } else {
        // First time seeing this transaction
        const txWithIndex = { ...tx, _originalIndex: index };
        seen.set(key, txWithIndex);
      }
    });
    
    return duplicates;
  };

  const fetchIncomeTransactions = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
      const params = new URLSearchParams();
      
      if (filters.transaction_type) params.append('transaction_type', filters.transaction_type);
      if (filters.property_id) params.append('property_id', filters.property_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.tax_year) params.append('tax_year', filters.tax_year);

      const response = await axios.get(`/api/income?${params.toString()}`, { headers });
      if (response.data.success) {
        const transactions = response.data.data || [];
        setIncomeTransactions(transactions);

        // Auto-apply income rules
        applyIncomeRules(transactions);

        // Check for duplicates
        const duplicates = detectDuplicates(transactions);
        if (!suppressDuplicatesNextFetch) {
          if (duplicates.length > 0) {
            setDuplicateModal({ duplicates });
          }
        } else {
          // Clear suppression after one fetch cycle
          setSuppressDuplicatesNextFetch(false);
        }
      }
    } catch (error) {
      console.error('Error fetching income transactions:', error);
      toast.error('Failed to load income transactions');
    } finally {
      setLoading(false);
    }
  };

  // Apply rules to mark transaction_type and property_id
  const applyIncomeRules = async (transactions) => {
    for (const tx of transactions) {
      const key = normalizeDescription(tx.description);
      const rule = incomeRules[key];
      if (!rule) continue;
      const desiredType = rule.transaction_type;
      const desiredProp = desiredType === 'rental' ? (rule.property_id || null) : undefined;
      const currentType = tx.transaction_type;
      const currentProp = tx.property_id || null;
      const needsType = desiredType && desiredType !== currentType;
      const needsProp = desiredType === 'rental' && desiredProp !== undefined && desiredProp !== currentProp;
      if (needsType || needsProp) {
        const payload = { };
        if (needsType) payload.transaction_type = desiredType;
        if (needsProp) payload.property_id = desiredProp;
        try {
          await updateIncomeTransaction(tx.id, payload);
        } catch (e) {
          // ignore individual update failures
        }
      }
    }
  };

  const fetchProperties = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
      const response = await axios.get('/api/properties', { headers });
      if (response.data.success) {
        // API returns properties in response.data.data or response.data.properties
        const props = response.data.data || response.data.properties || [];
        setProperties(props);
        console.log('✅ Properties loaded:', props.length, props);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    }
  };

  const updateIncomeTransaction = async (id, updates) => {
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
      const response = await axios.put(`/api/income/${id}`, updates, { headers });
      
      if (response.data.success) {
        toast.success('Income transaction updated');
        fetchIncomeTransactions();
        setEditingId(null);
        setEditingData({}); // Clear editing data
      } else {
        toast.error(response.data.message || 'Failed to update transaction');
      }
    } catch (error) {
      console.error('Error updating income transaction:', error);
      toast.error('Failed to update transaction: ' + (error.response?.data?.message || error.message));
    }
  };

  const saveEditingTransaction = async (id) => {
    if (editingData[id] && Object.keys(editingData[id]).length > 0) {
      // Get the transaction type (either from editing data or current transaction)
      const transaction = incomeTransactions.find(t => t.id === id);
      const newTransactionType = editingData[id].transaction_type;
      const currentTransactionType = transaction?.transaction_type;
      const finalTransactionType = newTransactionType || currentTransactionType;
      
      // Prepare update payload
      const updatePayload = { ...editingData[id] };
      
      // If changing transaction type to non-rental, don't send property_id
      // (backend will clear it automatically when type changes)
      if (newTransactionType && newTransactionType !== 'rental' && 'property_id' in updatePayload) {
        delete updatePayload.property_id;
      }
      
      // If transaction type is not rental and property_id is in payload, remove it
      if (finalTransactionType !== 'rental' && 'property_id' in updatePayload) {
        delete updatePayload.property_id;
      }
      
      // Suppress duplicate popup on the next refresh triggered by this save
      setSuppressDuplicatesNextFetch(true);
      await updateIncomeTransaction(id, updatePayload);
    } else {
      // No changes, just exit edit mode
      setEditingId(null);
      setEditingData({});
    }
  };

  const deleteIncomeTransaction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this income transaction?')) {
      return;
    }

    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
      const response = await axios.delete(`/api/income/${id}`, { headers });
      
      if (response.data.success) {
        toast.success('Income transaction deleted');
        setSuppressDuplicatesNextFetch(true);
        fetchIncomeTransactions();
      } else {
        toast.error(response.data.message || 'Failed to delete transaction');
      }
    } catch (error) {
      console.error('Error deleting income transaction:', error);
      toast.error('Failed to delete transaction: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getIncomeTypeLabel = (type) => {
    const labels = {
      'w2_wages': 'W-2 Wages',
      'rental': 'Rental Income',
      'dividend': 'Dividend',
      'interest': 'Interest',
      'capital_gain': 'Capital Gains',
      'capital_loss': 'Capital Loss',
      'roth_withdrawal': 'Roth Withdrawal',
      'business_income': 'Business Income',
      'other': 'Other Income'
    };
    return labels[type] || type;
  };

  const incomeTypes = [
    { value: 'w2_wages', label: 'W-2 Wages' },
    { value: 'rental', label: 'Rental Income' },
    { value: 'dividend', label: 'Dividend' },
    { value: 'interest', label: 'Interest' },
    { value: 'capital_gain', label: 'Capital Gains' },
    { value: 'capital_loss', label: 'Capital Loss' },
    { value: 'roth_withdrawal', label: 'Roth Withdrawal' },
    { value: 'business_income', label: 'Business Income' },
    { value: 'other', label: 'Other Income' }
  ];

  const rentalProperties = properties.filter(p => p.type === 'rental');

  const handleDeleteDuplicate = async (duplicateId) => {
    try {
      await deleteIncomeTransaction(duplicateId);
      // Remove from duplicate modal
      setDuplicateModal(prev => {
        const updatedDuplicates = prev.duplicates.filter(d => d.transaction.id !== duplicateId);
        if (updatedDuplicates.length === 0) {
          return null; // Close modal if no more duplicates
        }
        return { duplicates: updatedDuplicates };
      });
      // Refresh the list
      fetchIncomeTransactions();
    } catch (error) {
      console.error('Error deleting duplicate:', error);
    }
  };

  const handleAcceptDuplicates = () => {
    // Just close the modal - user accepts duplicates
    setDuplicateModal(null);
    toast('Duplicates accepted. You can delete them individually if needed.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading income transactions...</p>
        </div>
      </div>
    );
  }

  // Duplicate Review Modal
  const DuplicateModal = () => {
    if (!duplicateModal) return null;
    
    return (
      <div
        onClick={() => setDuplicateModal(null)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '20px', fontWeight: 'bold', color: '#dc2626' }}>
            ⚠️ Duplicate Income Transactions Found
          </h3>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            The following transactions have the same date and description. You can delete them individually or accept all duplicates.
          </p>
          
          <div style={{ marginBottom: '20px', maxHeight: '400px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', minWidth: '600px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead style={{ background: '#f9fafb', position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Date</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', minWidth: '200px' }}>Description</th>
                  <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Type</th>
                  <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap' }}>Amount</th>
                  <th style={{ padding: '12px 8px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: '#374151', whiteSpace: 'nowrap', width: '120px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {duplicateModal.duplicates.map((dup, index) => {
                  if (dup._deleted) return null;
                  const tx = dup.transaction;
                  return (
                    <tr key={`${tx.id}-${index}`} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 8px', fontSize: '13px', color: '#374151' }}>
                        {formatDate(tx.transaction_date)}
                      </td>
                      <td style={{ padding: '12px 8px', fontSize: '13px', color: '#374151' }}>
                        {tx.description}
                      </td>
                      <td style={{ padding: '12px 8px', fontSize: '13px', color: '#374151' }}>
                        {getIncomeTypeLabel(tx.transaction_type)}
                      </td>
                      <td style={{ padding: '12px 8px', fontSize: '13px', color: '#374151', textAlign: 'right', fontWeight: '500' }}>
                        {formatCurrency(tx.amount)}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleDeleteDuplicate(tx.id)}
                          style={{
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Delete this duplicate"
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
            <button
              onClick={() => setDuplicateModal(null)}
              style={{
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAcceptDuplicates}
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '10px 20px',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Accept All ({duplicateModal.duplicates.filter(d => !d._deleted).length} remaining)
            </button>
          </div>
        </div>
      </div>
    );
  };

  const totalIncome = incomeTransactions.reduce((sum, tx) => sum + (parseFloat(tx.amount) || 0), 0);
  const incomeByType = incomeTransactions.reduce((acc, tx) => {
    const type = tx.transaction_type || 'other';
    acc[type] = (acc[type] || 0) + (parseFloat(tx.amount) || 0);
    return acc;
  }, {});

  return (
    <>
      <DuplicateModal />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <BackToTaxFormsBanner />
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <DollarSign className="h-8 w-8 text-green-600" />
                <h1 className="text-3xl font-bold text-gray-900">Income Tracker</h1>
              </div>
              <Link
                to="/tax-forms"
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <FileText className="h-4 w-4" />
                View on Tax Forms
              </Link>
            </div>
          </div>
          <Disclaimer variant="tax" />

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-blue-600 font-medium">Total Income</div>
              <div className="text-2xl font-bold text-blue-900">{formatCurrency(totalIncome)}</div>
            </div>
            {Object.entries(incomeByType).slice(0, 3).map(([type, amount]) => (
              <div key={type} className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-green-600 font-medium">{getIncomeTypeLabel(type)}</div>
                <div className="text-2xl font-bold text-green-900">{formatCurrency(amount)}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Income Type</label>
              <select
                value={filters.transaction_type}
                onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {incomeTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Property</label>
              <select
                value={filters.property_id}
                onChange={(e) => setFilters({ ...filters, property_id: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Properties</option>
                {rentalProperties.map(prop => (
                  <option key={prop.id} value={prop.id}>{prop.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Year</label>
              <select
                value={filters.tax_year}
                onChange={(e) => setFilters({ ...filters, tax_year: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Years</option>
                {getTaxYearOptions().map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Income Transactions Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {incomeTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                      No income transactions found{filters.tax_year ? '. Try "All Years" to see transactions from other years.' : '.'}
                    </td>
                  </tr>
                ) : (
                  incomeTransactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-gray-50">
                      {editingId === transaction.id ? (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <input
                              type="date"
                              value={editingData[transaction.id]?.transaction_date !== undefined ? editingData[transaction.id].transaction_date : transaction.transaction_date}
                              onChange={(e) => {
                                setEditingData(prev => ({
                                  ...prev,
                                  [transaction.id]: {
                                    ...prev[transaction.id],
                                    transaction_date: e.target.value
                                  }
                                }));
                              }}
                              className="w-full px-2 py-1 border border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <input
                              type="text"
                              value={editingData[transaction.id]?.description !== undefined ? editingData[transaction.id].description : transaction.description}
                              onChange={(e) => {
                                setEditingData(prev => ({
                                  ...prev,
                                  [transaction.id]: {
                                    ...prev[transaction.id],
                                    description: e.target.value
                                  }
                                }));
                              }}
                              className="w-full px-2 py-1 border border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <select
                              value={editingData[transaction.id]?.transaction_type || transaction.transaction_type}
                              onChange={(e) => {
                                const transaction_type = e.target.value;
                                setEditingData(prev => ({
                                  ...prev,
                                  [transaction.id]: {
                                    ...prev[transaction.id],
                                    transaction_type,
                                    property_id: transaction_type === 'rental' ? (prev[transaction.id]?.property_id || transaction.property_id) : null
                                  }
                                }));
                              }}
                              className="w-full px-2 py-1 border border-gray-300 rounded"
                            >
                              {incomeTypes.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {((editingData[transaction.id]?.transaction_type || transaction.transaction_type) === 'rental') ? (
                              <select
                                value={editingData[transaction.id]?.property_id !== undefined ? editingData[transaction.id].property_id : (transaction.property_id || '')}
                                onChange={(e) => {
                                  const property_id = e.target.value ? parseInt(e.target.value) : null;
                                  setEditingData(prev => ({
                                    ...prev,
                                    [transaction.id]: {
                                      ...prev[transaction.id],
                                      property_id
                                    }
                                  }));
                                }}
                                className="w-full px-2 py-1 border border-gray-300 rounded"
                              >
                                <option value="">Select Property</option>
                                {rentalProperties.map(prop => (
                                  <option key={prop.id} value={prop.id}>
                                    {prop.name || `${prop.address || ''} ${prop.city || ''} ${prop.state || ''}`.trim() || `Property ${prop.id}`}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <input
                              type="number"
                              step="0.01"
                              value={editingData[transaction.id]?.amount !== undefined ? editingData[transaction.id].amount : transaction.amount}
                              onChange={(e) => {
                                const amount = parseFloat(e.target.value);
                                if (!isNaN(amount)) {
                                  setEditingData(prev => ({
                                    ...prev,
                                    [transaction.id]: {
                                      ...prev[transaction.id],
                                      amount
                                    }
                                  }));
                                }
                              }}
                              className="w-full px-2 py-1 border border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => saveEditingTransaction(transaction.id)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Done
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(transaction.transaction_date)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {transaction.description}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {getIncomeTypeLabel(transaction.transaction_type)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {transaction.property_id ? (
                              <span className="flex items-center gap-1">
                                <Building className="h-4 w-4" />
                                {properties.find(p => p.id === transaction.property_id)?.name || 'Unknown'}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            {formatCurrency(transaction.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setEditingId(transaction.id);
                                setEditingData({ [transaction.id]: {} }); // Initialize empty editing state
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                              <button
                                onClick={async () => {
                                  const t = incomeTransactions.find(t => t.id === transaction.id) || transaction;
                                  const txType = (editingData[transaction.id]?.transaction_type) || t.transaction_type;
                                  const propId = (editingData[transaction.id]?.property_id) ?? t.property_id ?? null;
                                  if (!txType) {
                                    toast.error('Set a type (e.g., Rental, W-2) before saving a rule');
                                    return;
                                  }
                                  try {
                                    const headers = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
                                    const payload = {
                                      type: 'general',
                                      pattern: normalizeDescription(t.description),
                                      category: 'income',
                                      additionalData: {
                                        transaction_type: txType,
                                        property_id: txType === 'rental' ? (propId || null) : null,
                                        ruleScope: 'income_tracker'
                                      }
                                    };
                                    const res = await axios.post('/api/learning-rules', payload, { headers });
                                    if (res.data?.success) {
                                      toast.success('Income rule saved');
                                      // Refresh rules and try to apply immediately
                                      await loadIncomeRules();
                                      await applyIncomeRules(incomeTransactions);
                                    } else {
                                      toast.error('Failed to save rule');
                                    }
                                  } catch (e) {
                                    toast.error('Failed to save rule');
                                  }
                                }}
                                className="text-green-600 hover:text-green-800"
                                title="Save income rule for this description"
                              >
                                Rule
                              </button>
                              <button
                                onClick={() => deleteIncomeTransaction(transaction.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default IncomeTracker;

